import PropTypes from 'prop-types';
import React from 'react';

import { MenuItem } from '../../../components/MenuItem';
import { SideMenu } from '../../../components/SideMenu';
import Strings from '../../../Strings';
import { isCapManager, isDoctor, isSuperUser, isShipper, isPatient, isOrganizationManager } from '../../../utils/userRoles';
import { canViewCohorts, hasAnyPermissionInGroup, hasPermission, ORG_FEATURES, orgFeatureEnabled, orgFeatureValue, PERMISSIONS, PERMISSION_GROUPS } from '../../../utils/userPermissions';
import { isAccessProgram } from '../../../utils/cmsPrograms';
import { isDevelopment } from '../../../utils/environment';
import { root as devicesRoot } from '../../../pages/ShipperCaps';
import { root as provisioningRoot } from '../../../pages/SuperUser/Provisioning/Provisionings';
import { isEsi } from '../../../utils/portalType';
import { DeviceClass, DeviceTypes } from '../../../utils/deviceTypes';
import { PatientTabs } from '../../../pages/SuperUser/Patients';
import { ShippingTabs } from '../../../pages/Shipping';
import { root as superuserHealthRoot } from '../../../pages/SuperUser/SuperuserHealthCenter/SuperuserHealthCenter';
import { DeviceInvoicesMainTabs } from '../../../pages/SuperUser/DeviceInvoices/helpers';
import { DevTabs } from '../../../pages/SuperUser/Dev/Dev';

const addProvisioning = items => {
  const children = [];
  let firstAvailableUrl = '';
  Object.values(DeviceTypes)
    .filter(d => d.deviceClass === DeviceClass.cap || d.deviceClass === DeviceClass.hub)
    .forEach(deviceType => {
      if (hasPermission(deviceType.permission)) {
        children.push({
          tag: `${provisioningRoot}-${deviceType.key}`,
          label: deviceType.displayNamePlural,
          to: `/${provisioningRoot}/${deviceType.id}`,
          isActive: path => path.startsWith(`/${provisioningRoot}/${deviceType.id}`),
        });
        if (firstAvailableUrl == '') firstAvailableUrl = `/${provisioningRoot}/${deviceType.id}`;
      }
    });
  children.length > 0 &&
    items.push({
      tag: 'provisioning',
      label: Strings.provisioning,
      to: firstAvailableUrl,
      isActive: path => path.startsWith(`/${provisioningRoot}`),
      expanded: false,
      expandable: true,
      children,
    });
};

const addReport = items => {
  items.push({
    tag: 'report',
    label: Strings.report,
    to: '/report',
    isActive: path => path.startsWith(`/report`),
  });
};

const addBusinessReport = items => {
  items.push({
    tag: 'business-report',
    label: Strings.businessReport,
    to: '/business-report',
    isActive: path => path.startsWith('/business-report'),
  });
};
const addDashboardDoctor = items => {
  items.push({
    tag: 'dashboard',
    isActive: path => path.startsWith('/dashboard'),
    label: Strings.dashboard,
    to: '/dashboard',
  });
};
const addDashboardPatient = (items, patientId) => {
  items.push({
    tag: 'dashboard',
    isActive: path => path.startsWith(`/dashboard/${patientId}`),
    label: Strings.dashboard,
    to: '/user-dashboard',
  });
};
const addDashboardGeneral = items => {
  items.push({
    tag: 'dashboard',
    isActive: path => path.startsWith('/dashboard-devices'),
    label: Strings.dashboard,
    to: '/dashboard-devices',
  });
};
const addDashboardRpm = items => {
  items.push({
    tag: 'dashboard',
    isActive: path => path.startsWith('/dashboard-rpm'),
    label: Strings.dashboard,
    to: '/dashboard-rpm',
  });
};
const addStudies = items => {
  items.push({
    tag: 'studies',
    isActive: path => path.startsWith('/studies') || path.startsWith('/study'),
    label: Strings.studies,
    to: '/studies',
  });
};
const addPatientsEsi = items => {
  const children = [];
  let firstAvailableUrl = '';
  if (hasPermission(PERMISSIONS.LEGACY_PATIENTS_ACTIVE)) {
    const url = '/esi_patients/active';
    children.push({
      tag: 'patients-active',
      label: Strings.esi.active,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.LEGACY_PATIENTS_PENDING)) {
    const url = '/esi_patients/pending';
    children.push({
      tag: 'patients-pending',
      label: Strings.esi.pending,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.LEGACY_PATIENTS_TERMED)) {
    const url = '/esi_patients/termed';
    children.push({
      tag: 'patients-termed',
      label: Strings.esi.termed,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  children.length > 0 &&
    items.push({
      tag: 'patients',
      label: Strings.patients,
      to: firstAvailableUrl,
      isActive: path => path.startsWith('/esi_patients/'),
      expanded: false,
      expandable: true,
      children,
    });
};
const addMedications = (items, patientId) => {
  items.push({
    tag: 'medications',
    label: Strings.medications,
    to: `/patient/${patientId}/medications`,
    isActive: path => path.startsWith(`/patient/${patientId}/medications`),
  });
};
const addCalendar = items => {
  items.push({
    tag: 'calendar',
    label: Strings.calendar,
    to: '/calendar',
  });
};
const addHistory = items => {
  items.push({
    tag: 'history',
    label: Strings.history,
    to: '/history',
    path: path => path.startsWith(`/history`),
  });
};
const addCapPatients = items => {
  const children = [];
  let firstAvailableUrl = '';
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST)) {
    const url = PatientTabs.enrollment.path;
    children.push({
      tag: 'patient-list',
      label: PatientTabs.enrollment.name,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST) && canViewCohorts()) {
    const url = PatientTabs.cohorts.path;
    children.push({
      tag: 'cohorts',
      label: PatientTabs.cohorts.name,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST)) {
    const url = PatientTabs.patientFiles.path;
    children.push({
      tag: 'patient-files',
      label: PatientTabs.patientFiles.name,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.ORGANIZATION_PROVIDER_VIEW)) {
    const url = PatientTabs.careProviders.path;
    children.push({
      tag: PatientTabs.careProviders.key,
      label: PatientTabs.careProviders.name,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST) && isSuperUser()) {
    const url = PatientTabs.bulkUpload.path;
    children.push({
      tag: 'bulk-upload',
      label: PatientTabs.bulkUpload.name,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }

  children.length > 0 &&
    items.push({
      tag: 'patients',
      label: Strings.patients,
      to: firstAvailableUrl,
      isActive: path => path.startsWith('/cap-patients/'),
      expanded: false,
      expandable: true,
      children,
    });
};
const addAlerts = items => {
  const children = [];
  true &&
    children.push({
      tag: 'alerts-current',
      label: Strings.alerts,
      to: '/alerts/current',
      isActive: path => path.startsWith('/alerts/current'),
    });
  true &&
    children.push({
      tag: 'alerts-configurations',
      label: Strings.configuration,
      to: '/alerts/configuration',
      isActive: path => path.startsWith('/alerts/configuration'),
    });
  children.length > 0 &&
    items.push({
      tag: 'alerts',
      label: Strings.alerts,
      to: '/alerts/current',
      isActive: path => path.startsWith('/alerts/'),
      expanded: false,
      expandable: true,
      children,
    });
};
const addBilling = items => {
  items.push({
    tag: 'billing',
    label: Strings.billing.billing,
    to: '/billings',
    isActive: path => path.startsWith('/billings'),
  });
};
const addAccessGCodes = items => {
  items.push({
    tag: 'access-gcodes',
    label: Strings.accessGCodesTitle,
    to: '/access-gcodes',
    isActive: path => path.startsWith('/access-gcodes'),
  });
};
const addPatientActions = items => {
  items.push({
    tag: 'patient-actions',
    label: Strings.patientActions.title,
    to: '/actions',
    isActive: path => path.startsWith('/actions'),
  });
};
const addSurveys = items => {
  items.push({
    tag: 'surveys',
    label: Strings.surveys.surveys,
    to: '/surveys',
    isActive: path => path.startsWith('/surveys'),
  });
};
const organizationSupportsAccess = () => (
  (orgFeatureValue(ORG_FEATURES.supported_subprograms) || []).some(isAccessProgram)
);
const addMonitoring = items => {
  items.push({
    tag: 'monitoring',
    label: Strings.monitoring,
    to: '/monitoring',
    isActive: path => path.startsWith('/monitoring'),
  });
};
const addMessages = items => {
  const children = [];
  let firstAvailableUrl = '';
  if (hasPermission(PERMISSIONS.MESSAGES_INBOX)) {
    const url = '/messages/inbox';
    children.push({
      tag: 'messages-inbox',
      label: Strings.messages.inbox,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.MESSAGES_ARCHIVED)) {
    const url = '/messages/archived';
    children.push({
      tag: 'messages-archived',
      label: Strings.messages.archived,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.MESSAGES_SEND_MESSAGES)) {
    const url = '/messages/new';
    children.push({
      tag: 'messages-new',
      label: Strings.messages.new,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.MESSAGES_PREFABS)) {
    const url = '/messages/prefabs';
    children.push({
      tag: 'messages-prefabs',
      label: Strings.messages.prefabReplies,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }
  if (hasPermission(PERMISSIONS.MESSAGES_DEFAULT_REMINDERS_VIEW)) {
    const url = '/messages/default-reminders';
    children.push({
      tag: 'messages-default-reminders',
      label: Strings.defaultReminders,
      to: url,
      isActive: path => path.startsWith(url),
    });
    if (firstAvailableUrl == '') firstAvailableUrl = url;
  }

  children.length > 0 &&
    items.push({
      tag: 'messages',
      label: Strings.messages.messages,
      to: firstAvailableUrl,
      isActive: path => path.startsWith('/messages/'),
      expanded: false,
      expandable: true,
      children,
    });
};

const addDevices = items => {
  const children = [];
  let firstAvailableUrl = '';

  Object.values(DeviceTypes).forEach(deviceType => {
    if (hasPermission(deviceType.permission)) {
      const deviceUrl = `/devices/${encodeURIComponent(deviceType.id)}`;
      children.push({
        tag: `devices-${deviceType.key}`,
        label: deviceType.displayNamePlural,
        to: deviceUrl,
        isActive: path => decodeURIComponent(path).startsWith(decodeURIComponent(deviceUrl)),
      });
      if (firstAvailableUrl == '') firstAvailableUrl = deviceUrl;
    }
  });

  children.length > 0 &&
    items.push({
      tag: 'devices',
      label: Strings.deviceManagement,
      to: firstAvailableUrl,
      isActive: path => path.startsWith(`/${devicesRoot}/`),
      expanded: false,
      expandable: true,
      children,
    });
};

const addHealthCenter = items => {
  items.push({
    tag: 'health-center',
    key: 'health-center',
    label: Strings.healthCenter,
    isActive: path => path.startsWith('/health-center'),
    to: '/health-center',
  });
};

const addRpmMedications = items => {
  items.push({
    tag: 'medications',
    key: 'medications',
    label: Strings.medications,
    isActive: path => path.startsWith('/patient-medications'),
    to: '/patient-medications',
  });
};
const addRpmDevices = items => {
  items.push({
    tag: 'devices',
    key: 'devices',
    label: Strings.devices,
    isActive: path => path.startsWith('/patient-devices'),
    to: '/patient-devices',
  });
};

const addSettingsX = items => {
  items.push({
    tag: 'settings',
    key: 'right-menu-settings',
    label: Strings.settings,
    isActive: path =>
      path.startsWith('/esi-patient-settings') || path.startsWith('/user-settings') || path.startsWith('/settings'),
    to:
      // TODO, isPatinet is undefined, seems that /esi-patient-settings route is unavailable via sidebar
      isPatient() && isEsi()
        ? '/esi-patient-settings'
        : isDoctor() || isCapManager() || isSuperUser() || isShipper()
        ? '/user-settings'
        : '/settings',
  });
};
const addSupportX = items => {
  items.push({
    tag: 'support',
    key: 'support',
    label: Strings.support,
    isActive: path => path.startsWith('/support'),
    to: '/support',
  });
};

const addSystemConfiguration = items => {
  items.push({
    tag: 'system-configuration',
    key: 'system-configuration',
    label: Strings.systemConfiguration,
    isActive: path => path.startsWith('/system-configuration'),
    to: '/system-configuration/external-api-logs',
  });
};

const addSuperuserHealthCenter = items => {
  items.push({
    tag: 'superuser-health-center',
    key: 'superuser-health-center',
    label: Strings.superuserHealthCenter,
    isActive: path => path.startsWith(`/${superuserHealthRoot}/`),
    to: `/${superuserHealthRoot}/dashboard`,
  });
};

// Superuser-only debug tools.
const addDev = items => {
  items.push({
    tag: 'dev',
    key: 'dev',
    label: Strings.devTab,
    isActive: path => path.startsWith('/dev/'),
    to: DevTabs.AccessSimulator.path,
  });
};

const addAdmin = items => {
  const children = [];
  let firstAvailableUrl = '';

  if (hasPermission(PERMISSIONS.ORGANIZATION_ADMIN) || isSuperUser()) {
    const usersUrl = '/admin/users';
    children.push({
      tag: 'admin-users',
      label: Strings.users,
      to: usersUrl,
      isActive: path => path.startsWith(usersUrl),
    });
    if (firstAvailableUrl === '') firstAvailableUrl = usersUrl;
  }

  if (isSuperUser()) {
    const rolesUrl = '/admin/roles';
    children.push({
      tag: 'admin-roles',
      label: Strings.roles,
      to: rolesUrl,
      isActive: path => path.startsWith(rolesUrl),
    });
    if (firstAvailableUrl === '') firstAvailableUrl = rolesUrl;
  }

  if (hasAnyPermissionInGroup(PERMISSION_GROUPS.ORGANIZATION))
  {
    const organizationsUrl = '/admin/organizations';
    children.push({
      tag: 'admin-organizations',
      label: Strings.organizations,
      to: organizationsUrl,
      isActive: path => path.startsWith(organizationsUrl),
    });
    if (firstAvailableUrl === '') firstAvailableUrl = organizationsUrl;
  }

  if (hasPermission(PERMISSIONS.ORGANIZATION_MANAGEMENT) || isSuperUser())
  {
    const templatesUrl = '/admin/templates';
    children.push({
      tag: 'admin-templates',
      label: Strings.templates,
      to: templatesUrl,
      isActive: path => path.startsWith(templatesUrl),
    });
    if (firstAvailableUrl === '') firstAvailableUrl = templatesUrl;
  }

  if (hasPermission(PERMISSIONS.ORGANIZATION_GROUPING)) {
    const orgGroupsUrl = '/admin/org-groups';
    children.push({
      tag: 'admin-org-groups',
      label: Strings.orgGroups || 'Org Groups',
      to: orgGroupsUrl,
      isActive: path => path.startsWith(orgGroupsUrl),
    });
    if (firstAvailableUrl === '') firstAvailableUrl = orgGroupsUrl;

    if (isSuperUser()) {
      const featuresConfigUrl = '/admin/config/work-types';
      children.push({
        tag: 'admin-features-config',
        label: Strings.config || 'Config',
        to: featuresConfigUrl,
        isActive: path => path.startsWith(featuresConfigUrl),
      });
      if (firstAvailableUrl === '') firstAvailableUrl = featuresConfigUrl;
    }
  }

  children.length > 0 &&
    items.push({
      tag: 'admin',
      label: Strings.admin,
      to: firstAvailableUrl,
      isActive: path => path.startsWith('/admin/'),
      expanded: false,
      expandable: true,
      children,
    });
};

const addShipping = items => {
  const children = [];
  let firstAvailableUrl = '';
  Object.values(ShippingTabs).forEach(shippingTab => {
    if (shippingTab.permissions.some(permission => hasPermission(permission)) || isSuperUser() || isShipper()) {
      children.push({
        tag: shippingTab.key,
        label: shippingTab.name,
        to: shippingTab.url,
        isActive: path => path.startsWith(shippingTab.url),
      });
      if (firstAvailableUrl == '') firstAvailableUrl = shippingTab.url;
    }
  });

  children.length > 0 &&
    items.push({
      tag: 'shipping',
      label: Strings.shipping.shipping,
      to: firstAvailableUrl,
      isActive: path => path.startsWith('/shipping/'),
      expanded: false,
      expandable: true,
      children,
    });
};

const addDeviceInvoicing = items => {
  const children = [];
  let firstAvailableUrl = '';
  Object.values(DeviceInvoicesMainTabs).forEach(deviceInvoiceTab => {
    const canAccessPricing = deviceInvoiceTab.key !== DeviceInvoicesMainTabs.PRICING.key || isSuperUser() || isShipper();
    if (canAccessPricing && (hasPermission(deviceInvoiceTab.permission) || isSuperUser() || isShipper())) {
      children.push({
        tag: deviceInvoiceTab.key,
        label: deviceInvoiceTab.name,
        to: deviceInvoiceTab.url,
        isActive: path => path.startsWith(deviceInvoiceTab.url),
      });
      if (firstAvailableUrl == '') firstAvailableUrl = deviceInvoiceTab.url;
    }
  });

  children.length > 0 &&
    items.push({
      tag: 'device-invoices',
      label: Strings.deviceInvoices.deviceInvoices,
      to: firstAvailableUrl,
      isActive: path => path.startsWith('/device-invoices/'),
      expanded: false,
      expandable: true,
      children,
    });
};

export const getSidebarItems = (userId, forceAddReport = false) => {
  const items = [];

  // Only add ONE dashboard type per user to avoid duplicate menu items and redirect inconsistencies
  // Priority follows original order: Doctor Dashboard > Patient Dashboard > Devices Dashboard > RPM Dashboard
  if (hasAnyPermissionInGroup(PERMISSION_GROUPS.LEGACY_DOCTOR_DASHBOARD) && isDoctor()) {
    addDashboardDoctor(items);
  } else if (hasPermission(PERMISSIONS.LEGACY_PATIENT_PAGES_DASHBOARD)) {
    addDashboardPatient(items, userId);
  } else if (hasPermission(PERMISSIONS.DEVICES_DASHBOARD)) {
    addDashboardGeneral(items);
  } else if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_DASHBOARD_PROGRAMS)) {
    addDashboardRpm(items);
  }

  hasPermission(PERMISSION_GROUPS.STUDY) && addStudies(items);

  hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST) && addCapPatients(items);
  hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST) && organizationSupportsAccess() && addAccessGCodes(items);
  isDevelopment() && hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST) && addPatientActions(items);
  hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_READINGS) && addMonitoring(items);
  // Surveys go to patients, so they need the patient list
  hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST) && orgFeatureEnabled(ORG_FEATURES.surveys_enabled) && addSurveys(items);
  hasAnyPermissionInGroup(PERMISSION_GROUPS.BILLING) && addBilling(items);

  if (hasAnyPermissionInGroup(PERMISSION_GROUPS.ORGANIZATION) || hasPermission(PERMISSIONS.ORGANIZATION_GROUPING)) {
    addAdmin(items);
  }

  hasPermission(PERMISSIONS.DEVICES_PROVISIONING) && addProvisioning(items);

  if (isSuperUser() || isShipper() || hasAnyPermissionInGroup(PERMISSION_GROUPS.SHIPPING)) {
    addShipping(items);
  }
  if (isSuperUser() || isShipper() || hasPermission(PERMISSIONS.BILLING_DEVICE_INVOICES)) {
    addDeviceInvoicing(items);
  }

  hasAnyPermissionInGroup(PERMISSION_GROUPS.DEVICES) && addDevices(items);

  if (hasPermission(PERMISSIONS.REPORT) || forceAddReport){
    addReport(items);
  }
  if (isSuperUser()) {
    addBusinessReport(items);
  }

  hasAnyPermissionInGroup(PERMISSION_GROUPS.LEGACY_PATIENTS) && addPatientsEsi(items);

  hasPermission(PERMISSIONS.LEGACY_PATIENT_PAGES_MEDICATIONS) && addMedications(items, userId);
  hasPermission(PERMISSIONS.LEGACY_PATIENT_PAGES_CALENDAR) && addCalendar(items);
  hasPermission(PERMISSIONS.LEGACY_PATIENT_PAGES_HISTORY) && addHistory(items);
  hasAnyPermissionInGroup(PERMISSION_GROUPS.MESSAGES) && addMessages(items);
  hasPermission(PERMISSIONS.ALERTS) && addAlerts(items);

  hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_PAGES_HEALTH_CENTER) && addHealthCenter(items);
  hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_PAGES_MEDICATIONS) && addRpmMedications(items);
  hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_PAGES_DEVICES) && addRpmDevices(items);

  // Organization managers work the actions queue too, but keep landing on
  // their usual first page, so Actions comes after their other areas.
  isDevelopment() && !hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST) && isOrganizationManager() && addPatientActions(items);

  addSettingsX(items);
  addSupportX(items);

  hasPermission(PERMISSIONS.ORGANIZATION_CARE_PROVIDER_API) && addSystemConfiguration(items);
  isSuperUser() && addSuperuserHealthCenter(items);
  isSuperUser() && isDevelopment() && addDev(items);
  hasPermission(PERMISSIONS.ANALYTICS) && items.push({ tag: 'analytics', label: Strings.analytics, to: '/analytics' });
  return items;
};

const getRestrictedSidebarItems = () => {
  const items = [];
  addSettingsX(items);
  addSupportX(items);
  return items;
};
export function Sidebar(props) {
  const addLogout = () => {
    const { onLogOut } = props;
    return {
      tag: 'logout',
      label: Strings.signOut,
      onClick: onLogOut,
    };
  };

  const [items, setItems] = React.useState(
    (props.restrictFor2FASetup ? getRestrictedSidebarItems() : getSidebarItems(props.patientId, props.addReport)).concat([addLogout()]),
  );

  // Update sidebar items when access context changes
  React.useEffect(() => {
    const sidebarItems = props.restrictFor2FASetup
      ? getRestrictedSidebarItems()
      : getSidebarItems(props.patientId, props.addReport);
    setItems(sidebarItems.concat([addLogout()]));
  }, [props.patientId, props.addReport, props.restrictFor2FASetup]);

  const onExpand = params => {
    const newItems = [...items];
    const item = newItems.find(i => i.tag === params.tag);
    if (item !== undefined) {
      item.expanded = params.isExpanding;
      setItems(newItems);
    }
  };

  const { leftMenuVisible, location, onChange } = props;

  const effectiveItems = props.restrictFor2FASetup
    ? getRestrictedSidebarItems().concat([addLogout()])
    : items;

  const list = [];
  effectiveItems.forEach((data, index) => {
    list.push(<MenuItem key={data.tag} visible={leftMenuVisible} {...data} isChildNode={false} onExpand={onExpand} />);
    if (data.children !== undefined)
      data.children.forEach((chData, chIndex) => {
        data.expanded &&
          list.push(
            <MenuItem
              key={chData.tag}
              visible={leftMenuVisible}
              {...chData}
              isChildNode
              isParentExpanded={data.expanded}
              expandable={false}
              expanded={false}
            />,
          );
      });
  });

  return (
    <SideMenu visible={leftMenuVisible} path={location.pathname} onChange={onChange}>
      {list}
    </SideMenu>
  );
}

export default Sidebar;

Sidebar.propTypes = {
  visible: PropTypes.bool,
  location: PropTypes.object,
  onChange: PropTypes.func,
  onLogOut: PropTypes.func,
  patientId: PropTypes.any,
  restrictFor2FASetup: PropTypes.bool,
};
